import { TextLayout } from './text-layout'
import { LinearTransform } from '@st/geom'
// eslint-disable-next-line architecture/no-local-react-state -- Reusable PDF text field owns focus and its typing buffer; external values synchronize only while unfocused and edits emit onChange.
import { CSSProperties, useEffect, useState } from 'react'
import { match, P } from '@st/pattern'
import { PaintOp, RenderedAnnotation, RenderedPage } from './rendering'

export function HTMLRenderedPage({
  page,
  width,
  height,
  shouldRenderAnnotation
}: {
  page: RenderedPage
  width: number
  height: number
  shouldRenderAnnotation?: (annotation: RenderedAnnotation) => boolean
}) {
  let annotations = page.annotations
  if (shouldRenderAnnotation) {
    annotations = annotations.filter(shouldRenderAnnotation)
  }

  return (
    <div style={{ position: 'relative', width, height }}>
      <HTMLPaintElement paintOp={page.content} />
      {annotations.map((annotation, index) => (
        <HTMLAnnotation key={renderedAnnotationKey(annotation, index)} annotation={annotation} />
      ))}
    </div>
  )
}

function HTMLPaintElement({ paintOp }: { paintOp: PaintOp }) {
  return match(paintOp)
    .with({ type: 'text' }, (text) => (
      <span
        style={{
          ...absoluteBounds(text.bounds),
          fontFamily: text.font.family,
          fontWeight: text.font.weight,
          fontStyle: text.font.style,
          fontSize: text.font.size,
          color: text.color,
          textDecoration: textDecoration(text.line_through),
          whiteSpace: 'pre',
          overflow: 'visible',
          fontKerning: 'none',
          lineHeight: text.line_height,
          textAlign: text.text_align
        }}
        data-element-type="text"
      >
        {TextLayout.displayText(text.text, text.font)}
      </span>
    ))
    .with({ type: 'rect' }, (rect) => (
      <div
        style={{
          ...absoluteBounds(rect.bounds),
          borderColor: rect.border?.color,
          borderWidth: rect.border?.width,
          borderStyle: borderStyle(rect.border?.width),
          borderRadius: rect.border?.radius,
          backgroundColor: rect.background_color,
          boxSizing: 'border-box'
        }}
        data-element-type="rect"
      />
    ))
    .with({ type: 'path' }, (path) => (
      <svg
        style={{
          ...absoluteBounds(path.bounds)
        }}
        viewBox={`0 0 ${path.bounds.width} ${path.bounds.height}`}
        data-element-type="path"
      >
        <path
          d={path.path}
          fill={path.fill ?? 'none'}
          stroke={path.stroke}
          strokeWidth={path.stroke_width}
          transform={path.transform && LinearTransform.toSVG(path.transform)}
        />
      </svg>
    ))
    .with({ type: 'group' }, (group) => (
      <div
        style={{
          ...absoluteBounds(group.bounds)
        }}
        data-element-type="group"
      >
        {group.children.map((child, index) => (
          <HTMLPaintElement key={`${child.type}:${index}`} paintOp={child} />
        ))}
      </div>
    ))
    .exhaustive()
}

function renderedAnnotationKey(annotation: RenderedAnnotation, index: number) {
  return match(annotation)
    .with({ type: 'text_field' }, (value) => `${value.type}:${value.name}`)
    .with({ type: 'checkbox' }, (value) => `${value.type}:${value.name}`)
    .with({ type: 'select' }, (value) => `${value.type}:${value.name}`)
    .with({ type: 'radio' }, (value) => `${value.type}:${value.name}:${value.value}`)
    .with({ type: 'link' }, (value) => `${value.type}:${value.destination}`)
    .with(
      P.union({ type: 'comment' }, { type: 'rectangle' }, { type: 'text' }, { type: 'stamp' }),
      (value) => value.id ?? `${value.type}:${index}`
    )
    .exhaustive()
}

function HTMLAnnotation({ annotation }: { annotation: RenderedAnnotation }) {
  return match(annotation)
    .with({ type: 'link' }, (link) => (
      <a
        href={'#' + link.destination}
        style={absoluteBounds(link.bounds)}
        data-element-type="link"
      />
    ))
    .with({ type: 'text_field' }, (textField) => <HTMLTextField annotation={textField} />)
    .with({ type: 'checkbox' }, (checkbox) => (
      <input
        type="checkbox"
        name={checkbox.name}
        checked={checkbox.checked}
        onChange={(event) => checkbox.onChange?.(event.target.checked)}
        style={{
          ...absoluteBounds(checkbox.bounds),
          margin: 0,
          appearance: 'auto',
          accentColor: checkbox.background_color ?? '#000000',
          cursor: 'pointer',
          outline: 0
        }}
        data-element-type="checkbox"
      />
    ))
    .with({ type: 'radio' }, (radio) => (
      <input
        type="radio"
        name={radio.name}
        checked={radio.selected}
        onChange={(event) => radio.onChange?.(event.target.checked)}
        style={{
          ...absoluteBounds(radio.bounds),
          margin: 0,
          appearance: 'auto',
          accentColor: '#000000',
          cursor: 'pointer'
        }}
        data-element-type="radio"
      />
    ))
    .with({ type: 'select' }, (select) => (
      <select
        name={select.name}
        value={select.value ?? ''}
        onChange={(event) => select.onChange?.(event.target.value)}
        style={{
          ...absoluteBounds(select.bounds),
          border: 'none',
          outline: toHtmlBorder(select.border?.width, select.border?.color),
          borderRadius: select.border?.radius,
          backgroundColor: select.background_color,
          boxSizing: 'border-box',
          color: '#000000',
          fontFamily: 'Helvetica',
          fontSize: 10,
          lineHeight: 1.6,
          margin: 0,
          padding: '0 4px'
        }}
        data-element-type="select"
      >
        <option value="" />
        {select.options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    ))
    .with({ type: 'comment' }, (comment) => (
      <div
        style={{ ...absoluteBounds(comment.bounds), zIndex: 1 }}
        title={comment.content}
        aria-label={comment.content}
        data-element-type="comment"
        data-annotation-id={comment.id}
      >
        <svg
          style={{ color: comment.color, width: '100%', height: '100%' }}
          viewBox="0 0 38 38"
          fill="none"
        >
          <rect width="38" height="38" fill="currentColor" />
          <path d="M31 7H7V26H14.5V32L21 26H31V7Z" fill="white" />
          <rect x="10" y="14" width="17" height="1.5" fill="currentColor" />
          <rect x="10" y="18" width="14" height="1.5" fill="currentColor" />
        </svg>
      </div>
    ))
    .with(P.union({ type: 'rectangle' }, { type: 'text' }, { type: 'stamp' }), (appearance) => (
      <HTMLAnnotationAppearance annotation={appearance} />
    ))
    .exhaustive()
}

function HTMLAnnotationAppearance({
  annotation
}: {
  annotation: Extract<RenderedAnnotation, { appearance: PaintOp }>
}) {
  return (
    <div
      style={{ ...absoluteBounds(annotation.bounds), zIndex: 1 }}
      data-element-type={annotation.type}
      data-annotation-id={annotation.id}
    >
      <HTMLPaintElement paintOp={annotation.appearance} />
    </div>
  )
}

function HTMLTextField({
  annotation
}: {
  annotation: Extract<RenderedAnnotation, { type: 'text_field' }>
}) {
  const [focused, setFocused] = useState(false)
  const [value, setValue] = useState(annotation.value)

  useEffect(() => {
    if (!focused) setValue(annotation.value)
  }, [annotation.value, focused])

  let displayValue = value
  if (annotation.mask_char && !focused) {
    displayValue = value.replace(/./g, annotation.mask_char)
  }

  let lineHeight: string | undefined
  if (annotation.multiline && annotation.font_size) {
    lineHeight = `${annotation.font_size * 1.2}px`
  }

  const style: CSSProperties = {
    ...absoluteBounds(annotation.bounds),
    boxSizing: 'border-box',
    fontFamily: 'Helvetica',
    fontSize: annotation.font_size,
    border: 'none',
    outline: toHtmlBorder(annotation.border?.width, annotation.border?.color),
    borderRadius: annotation.border?.radius,
    backgroundColor: annotation.background_color,
    margin: 0,
    padding: 1,
    color: '#000000',
    lineHeight,
    resize: 'none'
  }

  if (annotation.bounds.width == 0 && annotation.bounds.height == 0) {
    return <input type="hidden" name={annotation.name} value={annotation.value} />
  }

  if (annotation.multiline) {
    return (
      <textarea
        name={annotation.name}
        value={displayValue}
        onChange={(event) => {
          setValue(event.target.value)
          annotation.onChange?.(event.target.value)
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={style}
        data-element-type="text_field"
      />
    )
  }

  return (
    <input
      type="text"
      name={annotation.name}
      value={displayValue}
      onChange={(event) => {
        setValue(event.target.value)
        annotation.onChange?.(event.target.value)
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={style}
      data-element-type="text_field"
    />
  )
}

function absoluteBounds(bounds: RenderedAnnotation['bounds']): CSSProperties {
  return {
    position: 'absolute',
    left: bounds.x,
    top: bounds.y,
    width: bounds.width,
    height: bounds.height
  }
}

function toHtmlBorder(width: number | undefined, color: string | undefined): string | undefined {
  if (!color || !width) return 'none'
  return `${width}px solid ${color}`
}

function textDecoration(lineThrough: boolean | undefined): CSSProperties['textDecoration'] {
  if (lineThrough) return 'line-through'
  return undefined
}

function borderStyle(width: number | undefined): CSSProperties['borderStyle'] {
  if (width) return 'solid'
  return 'none'
}
